import { Button } from "@narmi/design_system";
import React from "react";
import { Link, RouteObject, generatePath, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { PageContainer } from "../../components/PageContainer";
import { PermissionedRoutes, ValidatedRoutes } from "../../components/PermissionedRoutes";
import { TopBar } from "../../components/TopBar";
import { useUserConfiguration } from "../../hooks/useUserConfiguration";
import { CSRF_TOKEN } from "../csrf";

export const MARKETING_ROUTES: RouteObject[] = [
  {
    element: (
      <ValidatedRoutes
        validate={({ permissions }) =>
          permissions.includes("backoffice.update_marketing") ||
          permissions.includes("backoffice.view_marketing")
        }
      />
    ),
    children: [
      {
        index: true,
        async lazy() {
          const { CampaignList } = await import("./CampaignList");
          return {
            Component() {
              const createUuid = React.useMemo(() => uuidv4(), []);
              const { permissions } = useUserConfiguration();

              const [isNewPromotionDisabled, setIsNewPromotionDisabled] = React.useState(true);

              return (
                <>
                  <TopBar
                    title="Promotions"
                    action={
                      permissions.includes("backoffice.update_marketing") ? (
                        <Link
                          to={generatePath("/promotions/:campaignId/", {
                            campaignId: createUuid,
                          })}
                        >
                          <Button
                            size="xs"
                            label="New promotion"
                            disabled={isNewPromotionDisabled}
                          />
                        </Link>
                      ) : null
                    }
                  />
                  <PageContainer>
                    <CampaignList setIsNewPromotionDisabled={setIsNewPromotionDisabled} />
                  </PageContainer>
                </>
              );
            },
          };
        },
      },
      {
        element: <PermissionedRoutes requiredPermissions={["backoffice.update_marketing"]} />,
        children: [
          {
            path: ":campaignId/:section?",
            async lazy() {
              const { CreateForm } = await import("./create/CreateForm");
              return {
                Component() {
                  const params = useParams<{
                    campaignId: string;
                    section: string;
                  }>();
                  const navigate = useNavigate();

                  const [title, setTitle] = React.useState<string | null>(null);

                  return (
                    <>
                      {params.section === "review" ? (
                        <TopBar title="Review your promotion" />
                      ) : (
                        <TopBar
                          title={title ? `Edit ${title}` : "New promotion"}
                          backIconOnClick={() => navigate("/promotions")}
                        />
                      )}
                      <PageContainer>
                        <CreateForm
                          csrfToken={CSRF_TOKEN}
                          campaignId={params.campaignId!}
                          section={params.section ?? null}
                          setTitle={setTitle}
                        />
                      </PageContainer>
                    </>
                  );
                },
              };
            },
          },
        ],
      },
    ],
  },
];
